function _toConsumableArray(arr) { if (Array.isArray(arr)) { for (var i = 0, arr2 = Array(arr.length); i < arr.length; i++) { arr2[i] = arr[i]; } return arr2; } else { return Array.from(arr); } }

/**
 * Helper for stateful data and state manipulation.
 * This helper provides functions to serve following purposes:
 * <ul>
 *     <li>Introspect current application state and access it's internal state.
 *     <li>Provide access to parent state for child states.
 *     <li>Bind object lifecycle to state lifecycle and not to a "forever" cycle (as in the case of services).
 * </ul>
 *
 * In general, it should simplify "context-related" display (like top menus depending on the internal state) and
 * help to remove most of the "specific scenario" bugs. UI must be bound to the state and better to do that
 * implicitly (so <em>transition history</em> would not affect UI).
 */

export default ['$state', '$rootScope', '$timeout', '$q', function ($state, $rootScope, $timeout, $q) {
    var stateTransitionCallbacks = new Map();

    function getStateData(state) {
        /* Data field name. */
        var DATA_NAME = 'GW.PL.LiveData';

        if (!state.data) {
            state.data = {};
        }

        if (!state.data[DATA_NAME]) {
            state.data[DATA_NAME] = {};
        }
        return state.data[DATA_NAME];
    }

    function getParentStatesArray(state) {
        var collectedParentStates = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : [];

        collectedParentStates.unshift(state);
        if (state.parent === undefined) {
            return collectedParentStates;
        }
        var parentState = typeof state.parent === 'string' ? $state.get(state.parent) : state.parent;
        return getParentStatesArray(parentState, collectedParentStates);
    }

    function retrieveDataForStateName(stateName, includeInherited) {
        var _angular;

        var state = $state.get(stateName);
        if (!state) {
            return undefined;
        }
        var states = includeInherited ? getParentStatesArray(state) : [state];
        var statesData = states.map(function (_state) {
            return getStateData(_state);
        });

        // copying own enumerable properties from rightmost to leftmost parameters
        return (_angular = angular).extend.apply(_angular, _toConsumableArray(statesData));
    }

    function deleteDataForStateName(stateName, propertiesToDelete, includeInherited) {
        var state = $state.get(stateName);
        if (!state) {
            return undefined;
        }
        var states = includeInherited ? getParentStatesArray(state) : [state];
        var statesData = states.map(function (_state) {
            return getStateData(_state);
        });

        statesData.forEach(function (data) {
            var propsToDel = Object.getOwnPropertyNames(data);
            if (propertiesToDelete) {
                propsToDel = propsToDel.filter(function (prop) {
                    return propertiesToDelete.includes(prop);
                });
            }
            // set any matching properties to undefined
            propsToDel.forEach(function (prop) {
                data[prop] = undefined;
            });
        });
    }

    function stateHasParent(testState, parentState) {
        testState = $state.get(testState);
        parentState = $state.get(parentState);
        if (testState.name === parentState.name) {
            return true;
        }
        if (testState.parent === undefined) {
            return false;
        }
        return stateHasParent(testState.parent, parentState);
    }

    function isDefined(v) {
        return v !== null && v !== undefined;
    }

    function getTrackingUrlFromState(state) {
        var url = null;
        if (!state.data || !isDefined(state.data.trackingUrl)) return null;
        if (state.parent) {
            // Detects and discards trackingUrl's inherited from a parent state
            var parentState = $state.get(state.parent);
            url = parentState.data && state.data.trackingUrl === parentState.data.trackingUrl ? null : state.data.trackingUrl;
        } else {
            url = state.data.trackingUrl;
        }
        return url;
    }

    function buildTrackingPathSegment(state, params) {
        var pathSegment = void 0;
        var trackingUrl = getTrackingUrlFromState(state);
        if (trackingUrl !== null) {
            if (typeof trackingUrl === 'function') {
                var activeData = retrieveDataForStateName(state.name, true);
                var model = null;
                if (activeData.flowModel && activeData.model && activeData.model.value) {
                    model = activeData.model.value;
                }
                pathSegment = trackingUrl(state, params, model);
            } else {
                pathSegment = trackingUrl.toString();
            }
        } else if (isDefined(state.url)) {
            pathSegment = state.url;
        } else {
            pathSegment = '/' + state.name;
        }
        return pathSegment;
    }

    return {

        /**
         * Provides a location to store and access to data that relevant to the current state.
         * Will create a live data structure for the object if that structure do not already exists.
         *
         * @param {Boolean} [includeInherited=false] - Flag to indicate that data from parents should be included (defaults to false).
         *                                         Child properties will take precedence over parent properties of the same name
         *
         * @returns {Object|undefined} '<code>undefined</code>' if <code>stateName</code> was provided but current state is not compatible
         * with the requested state.
         */
        'activeDataForState': function activeDataForState() {
            var includeInherited = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : false;

            return retrieveDataForStateName($state.current.name, includeInherited);
        },


        /**
         * Deletes data being held in the current state object based on the <code>propertiesToDelete</code> parameter.
         *
         * @param {Array} [propertiesToDelete]       - Array of properties to be deleted from the active state object.
         *                                              If undefined then all active state data is deleted
         *
         * @param {Boolean} [includeInherited=false] -Flag to indicate that data from parent states also be deleted
         *
         * @returns {*}
         *
         */
        'removeActiveDataForState': function removeActiveDataForState(propertiesToDelete) {
            var includeInherited = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;

            return deleteDataForStateName($state.current.name, propertiesToDelete, includeInherited);
        },


        /**
         * Provides a location to store and access to data that relevant to a state specified by fnol.
         * Will create a data structure for the object if that structure does not already exist.
         *
         *  @param {String} stateName - name of the state to fetch data for.
         *  @param {Boolean} [includeInherited] - Flag to indicate that data from parents should be included (defaults to true).
         *                                         Child properties will take precedence over parent properties of the same name
         *
         * @throws Error if stateName parameter not provided
         * @throws Error if a state cannot be found for the stateName
         *
         * @returns {Object|undefined} new or existing data associated with the state given by stateName
         */
        'dataForState': function dataForState(stateName) {
            var includeInherited = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : true;

            if (!stateName) {
                throw new Error('A valid state name must be provided.');
            }
            var stateData = retrieveDataForStateName(stateName, includeInherited);

            if (!stateData) {
                throw new Error('Unable to find state with name ' + stateName);
            }
            return stateData;
        },


        /**
         *Deletes data being held in the state object identified by <code>stateName</code> based on the
         * <code>propertiesToDelete</code> parameter.
         *
         * @param {String} stateName - name of the state to delete data on.
         *
         * @param {Array} [propertiesToDelete]       - Array of properties to be deleted from the active state object.
         *                                              If undefined then all active state data is deleted
         *
         * @param {Boolean} [includeInherited=false] -Flag to indicate that data from parent states also be deleted
         *
         * @returns {*}
         *
         */
        'removeDataForState': function removeDataForState(stateName, propertiesToDelete) {
            var includeInherited = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;

            return deleteDataForStateName(stateName, propertiesToDelete, includeInherited);
        },


        /**
         * Checks if <code>equalOrDescendantState</code> is equal to <code>equalOrParentState</code> or is a descendant of <code>equalOrParentState</code>.
         * If equalOrDecendantState is undefined then tests against the current state
         *
         * @param {String} equalOrParentState parent state to check if equal or a parent to equalOrDescendantState
         * @param {String} [equalOrDescendantState]  Defaults to the current state
         *
         * @returns {boolean}
         *
         */
        'hasState': function hasState(equalOrParentState, equalOrDescendantState) {
            if (!equalOrDescendantState) {
                // use current state
                return $state.includes(equalOrParentState);
            }
            var state = $state.get(equalOrDescendantState);
            var parentState = $state.get(equalOrParentState);
            return equalOrParentState ? stateHasParent(state, parentState) : false;
        },


        /**
         * Calls a function on each state change but before each controller is initialized.
         * @param {Function} callback function to call.
         *
         * @returns {Function} function to remove the listener
         */
        'beforeStateInit': function beforeStateInit(callback) {
            return $rootScope.$on('$stateChangeSuccess', callback);
        },


        /**
         * Calls a function on each state change after state controller is initalized.
         * @param {Function} callback zero-arg function to call.
         * @returns {Function} function to remove the listener
         */
        'afterStateInit': function afterStateInit(callback) {
            return $rootScope.$on('$stateChangeSuccess', function () {
                $timeout(callback);
            });
        },


        /**
         * Calls a function before state change
         * @param {Function} callback function to call.
         *
         * @returns {Function} function to remove the listener
         */
        'beforeStateChangeStart': function beforeStateChangeStart(callback) {
            return $rootScope.$on('$stateChangeStart', callback);
        },


        /**
         * Returns the tracking URL for a given state and state parameters.
         *
         * @returns {string} - the tracking URL, as a concatenation of the tracking URLs for the state chain.
         */
        'getTrackingUrl': function getTrackingUrl() {
            var state = $state.current;
            var stateParams = $state.params;
            var currentState = state;
            var path = null;
            while (currentState) {
                var pathSegment = buildTrackingPathSegment(currentState, stateParams);
                if (pathSegment.startsWith('^')) {
                    pathSegment = pathSegment.substr(1);
                    currentState = null;
                }
                path = path ? '' + pathSegment + path : pathSegment;

                currentState = currentState && currentState.parent ? $state.get(currentState.parent) : null;
            }
            return path;
        },
        subscribeToStateTransitions: function subscribeToStateTransitions(callback) {
            var lastStateTransitionDeferred = void 0;

            function trackStateTransitionInitiated(actionHandlerObject) {
                lastStateTransitionDeferred = $q.defer();
                callback(actionHandlerObject, lastStateTransitionDeferred.promise);
            }

            function trackStateTransitionFinished(transitionDescription) {
                if (lastStateTransitionDeferred) {
                    lastStateTransitionDeferred.resolve(transitionDescription);
                }
            }

            var flowModel = this.activeDataForState(true).flowModel;
            if (!flowModel) {
                throw new Error('Cannot subscribe to state transitions as no flowModel is available.');
            }

            flowModel.addTransitionStartListener(trackStateTransitionInitiated);
            flowModel.addTransitionFinishListener(trackStateTransitionFinished);

            stateTransitionCallbacks.set(callback, function () {
                flowModel.removeTransitionStartListener(trackStateTransitionInitiated);
                flowModel.removeTransitionFinishListener(trackStateTransitionFinished);
            });
        },
        unsubscribeFromStateTransitions: function unsubscribeFromStateTransitions(callback) {
            var unbind = stateTransitionCallbacks.get(callback);
            stateTransitionCallbacks.delete(callback);

            unbind();
        },
        jumpToTheStep: function jumpToTheStep(step) {
            var flowModel = this.activeDataForState(true).flowModel;
            if (flowModel.isStepAccessibleFromCurrent(step)) {
                flowModel.jumpToStep(step, this.activeDataForState(true).model);
            }
        }
    };
}];


//////////////////
// WEBPACK FOOTER
// ./common/js/src/edge/api/gw-portals-util-angular/StateUtil.js
// module id = 929
// module chunks = 0